import React from 'react'
import ReactDOM from 'react-dom'
import leaflet from 'leaflet'

// the default leaflet.DivOverlay is an abstract class, we have to implement
// a little bit of stuff to be able to just slap a div onto the map
const LDivOverlay = leaflet.DivOverlay.extend({
  options: {
    interactive: false,
    offset: [0, 0],
    autoPanPadding: [80, 80]
  },
  initialize: function ({position, content, animatePosition, ...options}) {
    leaflet.setOptions(this, options)
    this.options.animatePosition = animatePosition
    this._latlng = leaflet.latLng(position)
    this._container = document.createElement('div')
    this._container.className = 'div-overlay-anchor leaflet-zoom-animated'
    this._container.style.position = 'absolute'
    this._contentNode = content
    this._container.appendChild(content)
  },
  onAdd: function (map) {
    // In order to not nuke performance by adding and removing ~1000 markers
    // from the DOM when we hit the 1-mile zoom level on a long route, we need
    // to prevent the initial call to this.getPane().appendChild(this._container)
    //
    // Unfortunately, this means that we can no longer delegate to the
    // leaflet.DivOverlay constructor, we have to copy it and tweak it.
    const visibleOnAdd = this._isVisible() || this.options.autoPan

    // BEGIN copied code from leaflet.DivOverlay.prototype.onAdd
    this._zoomAnimated = map._zoomAnimated

    clearTimeout(this._removeTimeout)
    if (visibleOnAdd) {
      this.getPane().appendChild(this._container)
      this.update()
      this.bringToFront()
    }
    // END copied code from leaflet.DivOverlay.prototype.onAdd

    if (this.options.animatePosition) {
      this.enableAnimation()
    }
    if (this.options.autoPan) {
      this._adjustPan(true)
    }
    this._map.on('resize moveend zoomend', this._updateVisibility, this)
    this._updateVisibility()
  },
  enableAnimation: function () {
    // the pending frame doesn't include the div
    window.requestAnimationFrame(() => {
      // the second pending frame now includes the div, let it render...
      window.requestAnimationFrame(() => {
        // ...and THEN set the transition
        this._container.style.transition = 'transform 150ms ease-in-out'
      })
    })
  },
  _isVisible: function () {
    if (!this._map) { return false }
    const map = this._map
    // remove markers from dom when they are off-map by a 50% padding factor.
    const virtualizationBounds = map.getBounds().pad(0.5)
    return virtualizationBounds.contains(this.getLatLng())
  },
  _updateVisibility: function () {
    if (!this._map) { return }
    const isVisible = this._isVisible()
    const pane = this.getPane()
    if (isVisible && !this._container.parentNode) {
      // if we aren't in the DOM and should be, add to dom
      this.update()
      pane.appendChild(this._container)
    } if (!isVisible && this._container.parentNode) {
      // if we are in the DOM and shouldn't be, remove from dom
      this._container.parentNode.removeChild(this._container)
    }
  },
  _updateLayout: function () {
    this._containerWidth = this._container.offsetWidth
  },
  _animateZoom: function (e) {
    const pos = this._map._latLngToNewLayerPoint(this._latlng, e.zoom, e.center)
    const anchor = this._getAnchor()
    this._container.style.transition = ''
    leaflet.DomUtil.setPosition(this._container, pos.add(anchor))
    if (this.options.animatePosition) {
      this.enableAnimation()
    }
  },
  _adjustPan: function (initialMount = false) {
    if (this.options.autoPan && initialMount) {
      leaflet.Popup.prototype._adjustPan.call(this)
    }
  }
})

export class LeafletDivOverlay extends React.PureComponent {
  static defaultProps = {
    offset: [0, 0],
    animatePosition: true
  }

  constructor (props) {
    super(props)
    this._container = document.createElement('div')
  }

  componentDidMount () {
    const {mapInstance, position, offset, pane, animatePosition, autoPan} = this.props
    this._lDivOverlay = new LDivOverlay({
      content: this._container,
      offset: leaflet.point(offset),
      pane,
      position,
      animatePosition,
      autoPan
    })
    this._lDivOverlay.addTo(mapInstance.leafletElement)
  }

  componentWillUnmount () {
    // todo - kill the _lDivOverlay
    const {mapInstance} = this.props
    mapInstance.leafletElement.removeLayer(this._lDivOverlay)
    delete this._lDivOverlay
  }

  componentDidUpdate () {
    const {position} = this.props
    this._lDivOverlay.setLatLng(position)
  }

  render () {
    const {children, minWidth} = this.props
    return ReactDOM.createPortal(<div style={{minWidth}}>{children}</div>, this._container)
  }
}
